import React, {
    useContext,
    useState,
    useEffect,
    useRef,
    useLayoutEffect,
} from 'react';
import { JsonRpcProvider, formatEther, Contract, formatUnits } from 'ethers';
import './cornercomponent.css';
import UserContext from '../UserContext';
import AccountContext from '../AccountContext';
import cornerImg from './Top_Left_Panel3.png';
import headLogoPixelImg from '../Head_Logo_Pixel2.png';

/* ---------- chain + token config ---------- */
const RPC_URL = 'https://rpc.mtv.ac';
const PROX_TOKEN_ADDRESS = '0x9029d1deda00e166de55406eab2608e4efc66d7b';
const ERC20_ABI = [
    'function balanceOf(address owner) view returns (uint256)',
    'function decimals() view returns (uint8)',
];

/* ---------- helpers ---------- */
const lerpRGB = (start, end, t) => {
    const r = Math.round(start[0] + (end[0] - start[0]) * t);
    const g = Math.round(start[1] + (end[1] - start[1]) * t);
    const b = Math.round(start[2] + (end[2] - start[2]) * t);
    return `rgb(${r}, ${g}, ${b})`;
};

const belowColor = (value, threshold) => {
    const v = Number(value) || 0;
    const deficit = Math.max(0, threshold - v);
    const t = Math.min(1, deficit / threshold);
    return lerpRGB([100, 149, 237], [255, 0, 0], t);
};

const fmt = (val) => {
    const num = Number(val);
    if (Number.isNaN(num)) return '0.00';
    return num.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

/* ---------- AutoFitRow ---------- */
const AutoFitRow = ({
    label,
    value,
    valueClassName,
    valueStyle,
    minPx = 15,
    maxPx = 24,
    title,
    gapPx,
}) => {
    const rowRef = useRef(null);
    const labelRef = useRef(null);
    const valueRef = useRef(null);

    const fontRef = useRef(maxPx);
    const fittingRef = useRef(false);
    const [valueFont, setValueFont] = useState(maxPx);
    const [valueWidth, setValueWidth] = useState(undefined);

    const applyFont = (px) => {
        const clamped = Math.max(minPx, Math.min(maxPx, Math.round(px)));
        if (clamped !== fontRef.current) {
            fontRef.current = clamped;
            setValueFont(clamped);
        }
    };

    const fit = () => {
        if (fittingRef.current) return;
        fittingRef.current = true;

        const row = rowRef.current;
        const lab = labelRef.current;
        const val = valueRef.current;
        if (!row || !lab || !val) {
            fittingRef.current = false;
            return;
        }

        const cs = getComputedStyle(row);
        const computedGap =
            gapPx != null ? Number(gapPx) : Number.parseFloat(cs.columnGap || cs.gap || '0') || 0;

        const safetyPx = 3;
        const rowW = Math.floor(row.clientWidth);
        const labW = Math.ceil(lab.scrollWidth);
        const available = Math.max(0, rowW - labW - computedGap - safetyPx);

        if (available !== valueWidth) setValueWidth(available);

        val.style.fontSize = `${fontRef.current}px`;
        val.style.whiteSpace = 'nowrap';

        const needed = Math.ceil(val.scrollWidth) || 1;
        if (available <= 0) {
            applyFont(minPx);
            fittingRef.current = false;
            return;
        }

        const ratio = available / needed;
        const TOL = 0.02;

        if (ratio < 1 - TOL) {
            const next = fontRef.current * ratio;
            applyFont(next);
        } else if (ratio > 1 + TOL && fontRef.current < maxPx) {
            const next = Math.min(maxPx, fontRef.current * Math.min(ratio, 1.1));
            applyFont(next);
        }

        fittingRef.current = false;
    };

    useLayoutEffect(() => {
        const run = () => requestAnimationFrame(fit);
        const ro = new ResizeObserver(run);

        if (rowRef.current) ro.observe(rowRef.current);
        if (labelRef.current) ro.observe(labelRef.current);

        const id = requestAnimationFrame(fit);
        return () => {
            cancelAnimationFrame(id);
            ro.disconnect();
        };
    }, [label, value, minPx, maxPx, gapPx]);

    return (
        <div className="fit-row" ref={rowRef} title={title ?? String(value)}>
            <span className="fit-label" ref={labelRef}>{label}</span>
            <span
                className={`fit-value ${valueClassName || ''}`}
                ref={valueRef}
                style={{
                    ...(valueStyle || {}),
                    width: valueWidth != null ? `${valueWidth}px` : undefined,
                    fontSize: `${valueFont}px`,
                }}
            >
                {value}
            </span>
        </div>
    );
};

/* ---------- live token balances ---------- */
const useTokenBalance = (walletAddress) => {
    const [balances, setBalances] = useState({ mtv: '0', prox: '0' });

    useEffect(() => {
        if (!walletAddress) return;
        const provider = new JsonRpcProvider(RPC_URL);
        const proxContract = new Contract(PROX_TOKEN_ADDRESS, ERC20_ABI, provider);

        let stopped = false;

        const fetchBalances = async () => {
            try {
                const [mtvBalBN, proxRaw, proxDec] = await Promise.all([
                    provider.getBalance(walletAddress),
                    proxContract.balanceOf(walletAddress),
                    proxContract.decimals(),
                ]);
                const mtv = formatEther(mtvBalBN);
                const prox = formatUnits(proxRaw, proxDec);
                if (!stopped) setBalances({ mtv, prox });
            } catch (err) {
                console.error('[CornerComponent] balance fetch error:', err);
            }
        };

        fetchBalances();
        const id = setInterval(fetchBalances, 5000);
        return () => {
            stopped = true;
            clearInterval(id);
        };
    }, [walletAddress]);

    return balances;
};

/* ---------- component ---------- */
const CornerComponent = () => {
    const { userInfo } = useContext(UserContext) || {};
    const { accountData } = useContext(AccountContext) || {};

    const overview = accountData?.account_overview || {};
    const wallet_address = overview.wallet_address || userInfo?.wallet_address || '';

    const account_level = Number(overview.account_level ?? 0);
    const armor_in_account = Number(overview.armor_in_account ?? 0);
    const ammo_in_account = Number(overview.ammo_in_account ?? 0);

    const { mtv, prox } = useTokenBalance(wallet_address);

    const armorThreshold = 100;
    const ammoThreshold = 200;

    const armorIsLow = armor_in_account < armorThreshold;
    const ammoIsLow = ammo_in_account < ammoThreshold;

    const armorStyle = armorIsLow
        ? { color: belowColor(armor_in_account, armorThreshold) }
        : { color: 'cornflowerblue' };
    const ammoStyle = ammoIsLow
        ? { color: belowColor(ammo_in_account, ammoThreshold) }
        : { color: 'cornflowerblue' };

    /* --- Random red-eye glow: 1�2 times per 5 minutes --- */
    const [isEyesGlowing, setIsEyesGlowing] = useState({ L: false, R: false });

    useEffect(() => {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReducedMotion) return;

        const TEST_MODE = false;                 // set true while tuning
        const rand = (min, max) => Math.random() * (max - min) + min;
        const sleep = (ms) => new Promise(r => setTimeout(r, ms));

        let cancelled = false;

        async function burst() {
            // how many quick flickers in this burst
            const flickers = Math.floor(rand(3, 6));
            const onlyOneEye = Math.random() < 0.35;      // sometimes only one eye
            const leadLeft = Math.random() < 0.5;

            for (let i = 0; i < flickers && !cancelled; i++) {
                // quick on -> optional long hold -> off
                const onRise = rand(60, 180);
                const hold = Math.random() < 0.35 ? rand(600, 1600) : rand(120, 420);
                const offFall = rand(60, 180);
                const desync = rand(10, 120);               // slight left/right offset

                if (onlyOneEye) {
                    if (leadLeft) setIsEyesGlowing(prev => ({ ...prev, L: true }));
                    else setIsEyesGlowing(prev => ({ ...prev, R: true }));
                } else {
                    // bring one up, then the other shortly after
                    setIsEyesGlowing(prev => ({ ...prev, L: true }));
                    await sleep(desync);
                    if (cancelled) return;
                    setIsEyesGlowing(prev => ({ ...prev, R: true }));
                }

                await sleep(onRise + hold);
                if (cancelled) return;

                if (onlyOneEye) {
                    if (leadLeft) setIsEyesGlowing(prev => ({ ...prev, L: false }));
                    else setIsEyesGlowing(prev => ({ ...prev, R: false }));
                } else {
                    setIsEyesGlowing(prev => ({ ...prev, L: false }));
                    await sleep(rand(0, 80));
                    if (cancelled) return;
                    setIsEyesGlowing(prev => ({ ...prev, R: false }));
                }

                await sleep(offFall + rand(80, 220));       // small gap before next flicker
            }

            // sometimes do a longer, steady �stare�
            if (!cancelled && Math.random() < 0.25) {
                const leftFirst = Math.random() < 0.5;
                if (leftFirst) {
                    setIsEyesGlowing(p => ({ ...p, L: true }));
                    await sleep(rand(60, 200));
                    setIsEyesGlowing(p => ({ ...p, R: true }));
                } else {
                    setIsEyesGlowing(p => ({ ...p, R: true }));
                    await sleep(rand(60, 200));
                    setIsEyesGlowing(p => ({ ...p, L: true }));
                }
                await sleep(TEST_MODE ? rand(800, 1500) : rand(2500, 4500));
                setIsEyesGlowing({ L: false, R: false });
            }
        }

        async function loop() {
            while (!cancelled) {
                // wait between bursts
                const wait = TEST_MODE ? rand(1200, 2500) : rand(20000, 45000);
                await sleep(wait);
                if (cancelled) break;
                await burst();
            }
        }

        // switch state shape to {L:boolean, R:boolean}
        setIsEyesGlowing({ L: false, R: false });
        loop();

        return () => { cancelled = true; setIsEyesGlowing({ L: false, R: false }); };
    }, []);

    return (
        <div className="corner-component">
            <img src={cornerImg} alt="Corner" className="corner-image" />

            {/* Plate behind the skull */}
            <div className="head-logo-plate" />

            {/* Skull logo + eye glows (kept inside the plate area) */}
            <div className="logo-wrap">
                <img src={headLogoPixelImg} alt="Logo" className="logo" />
                <span className={`eye left idle ${isEyesGlowing.L ? 'on' : ''}`} />
                <span className={`eye right idle ${isEyesGlowing.R ? 'on' : ''}`} />
            </div>

            {/* Currency container (live balances) */}
            <div className="currency-container">
                <div className="currency-row">
                    <AutoFitRow
                        label="PROX:"
                        value={fmt(prox)}
                        valueClassName="amount"
                        minPx={12}
                        title={fmt(prox)}
                    />
                </div>
                <div className="currency-row">
                    <AutoFitRow
                        label="MTV:"
                        value={fmt(mtv)}
                        valueClassName="amount"
                        minPx={12}
                        title={fmt(mtv)}
                    />
                </div>
            </div>

            {/* General stats */}
            <div className="corner-general-stats">
                <div className="currency-row">
                    <AutoFitRow
                        label="Account Level:"
                        value={account_level}
                        valueClassName="amount2 gold"
                        minPx={12}
                    />
                </div>
                <div className="currency-row">
                    <AutoFitRow
                        label="Armor Stored:"
                        value={armor_in_account}
                        valueClassName={`amount2 ${armorIsLow ? 'flash' : ''}`}
                        valueStyle={armorStyle}
                        minPx={12}
                    />
                </div>
                <div className="currency-row">
                    <AutoFitRow
                        label="Ammo Stored:"
                        value={ammo_in_account}
                        valueClassName={`amount2 ${ammoIsLow ? 'flash' : ''}`}
                        valueStyle={ammoStyle}
                        minPx={12}
                    />
                </div>
            </div>
        </div>
    );
};

export default CornerComponent;
